import { getWorkItemRedirectionPath } from '@jira/issue__eligibility/src/getWorkItemRedirectionPath.tsx';
import { isWorkItemModernizationGatesEnabled } from '@jira/issue__eligibility/src/isWorkItemModernizationGatesEnabled.tsx';
import { shouldShowWorkItemView } from '@jira/issue__eligibility/src/shouldShowWorkItemView.tsx';
import { issueViewSwitchDataFetcher } from '@jira/router-resources__work-item-view/src/issueViewSwitchDataFetcher.tsx';
import type { BifrostRedirectHandlerPayload } from '@jira/platform__routing-route-types/src/JiraSpaRoute.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';

/**
 * Bifrost redirect: fetch project type and user preference via a single AGG query,
 * then apply work-item / JSM redirection rules.
 */
export async function bifrostBrowseRouteRedirectionHandler({
	match,
	url,
	tenantContext,
	isEmbed,
}: BifrostRedirectHandlerPayload & { isEmbed?: boolean }): Promise<string | undefined> {
	if (
		!isWorkItemModernizationGatesEnabled() &&
		(!fg('jsm_aniv_routing_phase_1') || !fg('jsm_aniv_route_redirection'))
	) {
		return undefined;
	}

	if (url.search.includes('disableRedirect=true')) {
		return undefined;
	}

	try {
		const issueKey = match.params.issueKey;
		if (!issueKey) {
			return undefined;
		}

		const projectKey = issueKey.split('-')[0];

		// MWIV-814: prefer the canonical issue key from Jira to handle project key moves/renames.
		const {
			projectType,
			userPreference,
			issueKey: canonicalIssueKey,
			isCreatedFromAgenticTemplate,
			isAgenticWorkItemViewEnabled,
			jsmPractice,
		} = fg('wiv-fix-redirection-issue-key')
			? await issueViewSwitchDataFetcher(projectKey, tenantContext.cloudId, issueKey)
			: await issueViewSwitchDataFetcher(projectKey, tenantContext.cloudId);

		const fromIssueKey = fg('wiv-fix-redirection-issue-key')
			? (canonicalIssueKey ?? issueKey)
			: issueKey;

		const shouldShowWiv = shouldShowWorkItemView({
			cloudId: tenantContext.cloudId,
			issueKey,
			isIssueViewWivEnabled: userPreference,
			projectType,
			isCreatedFromAgenticTemplate,
			isAgenticWorkItemViewEnabled,
		});

		return getWorkItemRedirectionPath({
			issueKey: fromIssueKey,
			projectType,
			searchQuery: url.search,
			userPreference,
			isEmbed,
			wivEnabledForProject: shouldShowWiv,
			jsmPractice,
		});
	} catch (error) {
		log.safeErrorWithoutCustomerData(
			'issue.router-routes.browse.issue.route.entry',
			error instanceof Error ? error.message : 'Unable to redirect at SSR',
		);
	}
}
